
import React, { forwardRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Card,
  CardMedia,
  Divider,
  Grid,
  Paper,

} from '@mui/material';

import moment from 'moment';

const CheckinArticalPrint = forwardRef(({ 
  items, 
  damageStatus, 
  contentRef,
  damageCharges, 
  lostCharges, 
  remarks,
  bookingData,
  uploadedPhotos 
}, ref) => {

  const groupArticlesByCategory = (articles) => {
    const categories = {};
    articles.forEach((mainItem) => {
      if (mainItem.TrnChkArtList && mainItem.TrnChkArtList.length > 0) {
        mainItem.TrnChkArtList.forEach((article) => {
          if (!categories[article.ChkArtCatName]) {
            categories[article.ChkArtCatName] = [];
          }
          categories[article.ChkArtCatName].push({
            ...article,
            mainIndex: articles.indexOf(mainItem)
          });
        });
      }
    });
    return categories;
  };

  const getCategorySummary = (categoryArticles) => {
    let okCount = 0;
    let damagedCount = 0;
    let lostCount = 0;
    let totalDamageCharges = 0;
    let totalLostCharges = 0;

    categoryArticles.forEach((article) => {
      const articleKey = `${article.mainIndex}-${article.ChkArticleId}`;
      const status = damageStatus[articleKey] || 'O';
      
      if (status === 'O') {
        okCount++;
      } else if (status === 'D') {
        damagedCount++;
        totalDamageCharges += damageCharges[articleKey] || 0;
      } else if (status === 'L') {
        lostCount++;
        totalLostCharges += lostCharges[articleKey] || article.StdRate || 0;
      }
    });

    return {
      total: categoryArticles.length,
      ok: okCount,
      damaged: damagedCount,
      lost: lostCount,
      totalDamageCharges,
      totalLostCharges,
      totalCharges: totalDamageCharges + totalLostCharges
    };
  };


  const getStatusLabel = (status) => {
    switch(status) {
      case 'O': return 'Ok';
      case 'D': return 'Damaged';
      case 'L': return 'Lost';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'O': return '#4caf50';
      case 'D': return '#ff9800';
      case 'L': return '#f44336';
      default: return '#757575';
    }
  };

  const getTotalSummary = () => {
    let totalOk = 0;
    let totalDamaged = 0;
    let totalLost = 0;
    let totalDamageAmount = 0;
    let totalLostAmount = 0;

    items.forEach((mainItem, mainIndex) => {
      if (mainItem.TrnChkArtList) {
        mainItem.TrnChkArtList.forEach((article) => {
          const articleKey = `${mainIndex}-${article.ChkArticleId}`;
          const status = damageStatus[articleKey] || 'O';
          
          if (status === 'O') totalOk++;
          else if (status === 'D') {
            totalDamaged++;
            totalDamageAmount += damageCharges[articleKey] || 0;
          } else if (status === 'L') {
            totalLost++;
            totalLostAmount += lostCharges[articleKey] || article.StdRate || 0;
          }
        });
      }
    });

    return {
      totalOk,
      totalDamaged,
      totalLost,
      totalDamageAmount,
      totalLostAmount,
      grandTotal: totalDamageAmount + totalLostAmount,
      totalItems: totalOk + totalDamaged + totalLost
    };
  };


  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD/MM/YYYY');
  };

  const getChargeAmount = (articleKey, article) => {
    const status = damageStatus[articleKey] || 'O';
    if (status === 'D') {
      return damageCharges[articleKey] || 0;
    } else if (status === 'L') {
      return lostCharges[articleKey] || article.StdRate || 0;
    }
    return 0;
  };

  const summary = getTotalSummary();
  const allCategories = groupArticlesByCategory(items);

  return (
    <div ref={contentRef} style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
      <style>
        {`
          @media print {
            body {
              margin: 0;
              padding: 0;
              font-size: 12px;
            }
            .no-print {
              display: none !important;
            }
            @page {
              margin: 10mm;
              size: A4 portrait;
            }
            table {
              page-break-inside: avoid;
              font-size: 11px;
            }
            .page-break {
              page-break-before: always;
            }
            .accordion {
              break-inside: avoid;
            }
          }
          
          .status-ok {
            color: #4caf50;
            background-color: #e8f5e9;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: bold;
          }
          
          .status-damaged {
            color: #ff9800;
            background-color: #fff3e0;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: bold;
          }
          
          .status-lost {
            color: #f44336;
            background-color: #ffebee;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: bold;
          }
        `}
      </style>

      <Box sx={{ textAlign: 'center', mb: 3, borderBottom: '2px solid #1976d2', pb: 2 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold', fontSize: '24px' }}>
          CHECK-IN ARTICLES INVENTORY REPORT
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', mt: 1, fontSize: '16px' }}>
          Room Condition Checklist - Check-out Handover
        </Typography>
        <Typography variant="body2" sx={{ color: '#999', mt: 1, fontSize: '12px' }}>
          Generated on: {moment().format('DD/MM/YYYY hh:mm A')}
        </Typography>
      </Box>

      {bookingData && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: '16px', fontWeight: 'bold' }}>
            📋 BOOKING DETAILS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Booking No:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {bookingData.BookNo || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Resident Name:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {bookingData.ResidentName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Room/Bed:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {bookingData.RoombedName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Property:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {bookingData.PropName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Check-in Date:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatDate(bookingData.CheckinDt)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Mobile No:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {bookingData.MobileNo || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Email:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                {bookingData.Email || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Duration:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {bookingData.Duration || '0'} Months
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ mb: 4, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #bbdefb' }}>
        <Typography variant="h5" sx={{ mb: 2, color: '#1565c0', fontWeight: 'bold', textAlign: 'center' }}>
          📊 OVERALL SUMMARY
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>Total Items</Typography>
              <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {summary.totalItems}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#e8f5e9', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>✔ OK</Typography>
              <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {summary.totalOk}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#fff3e0', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#ef6c00', fontWeight: 'bold' }}>⚠ Damaged</Typography>
              <Typography variant="h4" sx={{ color: '#ef6c00', fontWeight: 'bold' }}>
                {summary.totalDamaged}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#ffebee', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 'bold' }}>❌ Lost</Typography>
              <Typography variant="h4" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                {summary.totalLost}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#f3e5f5', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>Damage Charges</Typography>
              <Typography variant="h4" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
                ₹{summary.totalDamageAmount.toFixed(2)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#e1f5fe', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: '#0277bd', fontWeight: 'bold' }}>Lost Charges</Typography>
              <Typography variant="h4" sx={{ color: '#0277bd', fontWeight: 'bold' }}>
                ₹{summary.totalLostAmount.toFixed(2)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#ffeb3b', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="h5" sx={{ color: '#f57f17', fontWeight: 'bold' }}>
                🏆 GRAND TOTAL CHARGES: ₹{summary.grandTotal.toFixed(2)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold', borderBottom: '2px solid #1976d2', pb: 1 }}>
          📦 ARTICLE DETAILS BY CATEGORY
        </Typography>

        {Object.keys(allCategories).length > 0 ? (
          Object.entries(allCategories).map(([categoryName, categoryArticles], index) => {
            const categorySummary = getCategorySummary(categoryArticles);
            
            return (
              <Paper 
                key={categoryName} 
                sx={{ 
                  mb: 3, 
                  border: '1px solid #e0e0e0',
                  pageBreakInside: 'avoid'
                }}
              >
                {/* Category Header */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#1976d2',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                    {categoryName.toUpperCase()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip 
                      label={`${categorySummary.ok} OK`}
                      sx={{ 
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                    <Chip 
                      label={`${categorySummary.damaged} Damaged`}
                      sx={{ 
                        backgroundColor: '#ff9800',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                    <Chip 
                      label={`${categorySummary.lost} Lost`}
                      sx={{ 
                        backgroundColor: '#f44336',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Total: {categorySummary.total} items
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Category Charges Summary:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666' }}>Damage Charges:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            ₹{categorySummary.totalDamageCharges.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666' }}>Lost Charges:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                            ₹{categorySummary.totalLostCharges.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666' }}>Total Charges:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            ₹{categorySummary.totalCharges.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', width: '30px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>ARTICLE NAME</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', width: '100px' }} align="center">STATUS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', width: '80px' }} align="right">STD RATE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', width: '100px' }} align="right">CHARGE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>REMARKS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '120px' }} align="center">CHECK-OUT PHOTO</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryArticles.map((article, idx) => {
                        const articleKey = `${article.mainIndex}-${article.ChkArticleId}`;
                        const status = damageStatus[articleKey] || 'O';
                        const statusLabel = getStatusLabel(status);
                        const statusColor = getStatusColor(status);
                        const chargeAmount = getChargeAmount(articleKey, article);
                        const displayImage = uploadedPhotos[articleKey] || article.ArtImg2;
                        const checkinImage = article.ArtImg1 ? article.ArtImg1.replace('http:/', 'http://') : null;

                        return (
                          <TableRow key={articleKey}>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 'bold' }}>
                              {idx + 1}
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {article.ChkArticleName || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                                ID: {article.ChkArticleId}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="center">
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: `${statusColor}15`,
                                  color: statusColor,
                                  fontWeight: 'bold',
                                  fontSize: '11px',
                                  border: `1px solid ${statusColor}`
                                }}
                              >
                                {statusLabel}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                ₹{(article.StdRate || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                              {chargeAmount > 0 ? (
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 'bold', 
                                  color: status === 'D' ? '#ff9800' : '#f44336' 
                                }}>
                                  ₹{chargeAmount.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                              <Typography variant="body2" sx={{ fontSize: '11px' }}>
                                {remarks[articleKey] || article.ArtRemk2 || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {displayImage ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Card sx={{ width: 70, height: 50, mb: 0.5 }}>
                                    <CardMedia
                                      component="img"
                                      height="50"
                                      image={displayImage}
                                      alt="Check-out"
                                      sx={{ objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/70x50?text=No+Image';
                                      }}
                                    />
                                  </Card>
                                  <Typography variant="caption" sx={{ color: '#666', fontSize: '9px' }}>
                                    Check-out
                                  </Typography>
                                </Box>
                              ) : checkinImage ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Card sx={{ width: 70, height: 50, mb: 0.5 }}>
                                    <CardMedia
                                      component="img"
                                      height="50"
                                      image={checkinImage}
                                      alt="Check-in"
                                      sx={{ objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/70x50?text=No+Image';
                                      }}
                                    />
                                  </Card>
                                  <Typography variant="caption" sx={{ color: '#666', fontSize: '9px' }}>
                                    Check-in
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                                  No Photo
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed #ccc', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ color: '#999' }}>
              No articles found for this booking
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer Section */}
      <Box sx={{ mt: 6, pt: 3, borderTop: '2px solid #1976d2' }}>
        <Typography variant="h6" sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold', textAlign: 'center' }}>
          APPROVAL SIGNATURES
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, minHeight: '120px' }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                RESIDENT SIGNATURE:
              </Typography>
              <Box sx={{ height: 60, borderBottom: '2px solid #000', mt: 3 }}></Box>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Name: {bookingData?.ResidentName || '_________________'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Date: _________________
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, minHeight: '120px' }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                PROPERTY MANAGER SIGNATURE:
              </Typography>
              <Box sx={{ height: 60, borderBottom: '2px solid #000', mt: 3 }}></Box>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Name: _________________
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Designation: _________________
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Date: _________________
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#d32f2f' }}>
            IMPORTANT NOTES:
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            1. This report must be signed by both parties (Resident & Property Manager).
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            2. All charges mentioned are final and non-negotiable.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            3. Damaged/Lost items will be charged as per standard rates.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            4. Security deposit adjustments will be made based on this report.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            5. Report generated on: {moment().format('DD/MM/YYYY HH:mm:ss')}
          </Typography>
        </Box>

        {/* Page Footer */}
        <Box sx={{ mt: 4, textAlign: 'center', borderTop: '1px solid #e0e0e0', pt: 2 }}>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            This is a computer-generated report. No physical signature required for digital acceptance.
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            Report ID: {bookingData?.BookNo ? `ART-${bookingData.BookNo}-${moment().format('YYYYMMDDHHmm')}` : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            Page 1 of 1
          </Typography>
        </Box>
      </Box>
    </div>
  );
});

CheckinArticalPrint.displayName = 'CheckinArticalPrint';

export default CheckinArticalPrint;